import PoolControls from "./PoolControls";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { dataProvider } from "../../providers/dataProvider";

type Job = {
  id: string;
  packet_id: string;
  state: string;
  workflow: string;
  environment: string;
  model: string;
  allocation_id: string;
  attempts: number;
  created_at: string;
};
type Allocation = {
  id: string;
  revoked: boolean;
  incarnation: string | null;
  queued_packets: number;
  active_leases: number;
  expired_leases: number;
};
export interface LlmOverview {
  gpu_rental?: { hourly_usd: number; pods: number; estimated: boolean };
  summary: {
    backlog_hours: number | null;
    assessments_per_hour: number | null;
    capacity_status: string;
    captured_at: string;
    queued: number;
    running: number;
    failed: number;
    succeeded: number;
    cancelled: number;
    arrivals_15m: number;
    finished_15m: number;
    succeeded_15m: number;
    failed_15m: number;
    oldest_pending_seconds: number | null;
    completed_per_minute: number;
    net_drain_per_minute: number;
    drain_seconds_no_new_work: number | null;
    historical_unknown_completion: number;
  };
  usage_15m: {
    attempts: number;
    failed_attempts: number;
    ambiguous_attempts: number;
    prompt_tokens: number;
    completion_tokens: number;
    cached_tokens: number;
  };
  workflows: {
    workflow: string;
    environment: string;
    queued: number;
    running: number;
    succeeded: number;
    failed: number;
  }[];
  allocations: Allocation[];
  timeline: {
    bucket: string;
    arrived: number;
    finished: number;
    failed: number;
    pending?: number;
  }[];
  jobs: Job[];
  total: number;
  shadow_admission: {
    pending_packets: number;
    submitted_packets: number;
    bridge_errors: number;
    last_update: string | null;
  };
  shadow_submissions: {
    id: string;
    org_name?: string;
    need_title?: string;
    processing?: boolean;
    match_run_id: number;
    state: string;
    need_id: number;
    baseline_batch_number: number;
    items_total: number;
    items_completed: number;
    items_failed: number;
    error: string | null;
  }[];
}
const stamp = (value: string) => new Date(value).toLocaleTimeString();

export default function LlmWorkDashboard() {
  const [data, setData] = useState<LlmOverview | null>(null);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let inFlight = false;
    const load = async () => {
      if (!active || inFlight || document.hidden) return;
      inFlight = true;
      setLoading(true);
      try {
        const result = await dataProvider.custom!({
          url: "/llm-work",
          method: "get",
          query: {},
        });
        if (active) {
          setData(result.data as unknown as LlmOverview);
          setError(false);
        }
      } catch {
        if (active) setError(true);
      } finally {
        inFlight = false;
        if (active) {
          setLoading(false);
          timer = setTimeout(load, 5000);
        }
      }
    };
    const onVisibility = () => {
      clearTimeout(timer);
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    void load();
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(timer);
    };
  }, [refresh]);
  const s = data?.summary;
  // Admission is packet-based; progress belongs to the run and appears only once.
  const runs = new Map<number, LlmOverview["shadow_submissions"][number]>();
  for (const row of data?.shadow_submissions ?? []) {
    const previous = runs.get(row.match_run_id);
    runs.set(row.match_run_id, {
      ...row,
      processing: row.processing || previous?.processing,
      error: row.error || previous?.error || null,
    });
  }
  const scans = new Map<string, LlmOverview["shadow_submissions"]>();
  for (const run of runs.values()) {
    const key = `${run.need_id}:${run.baseline_batch_number}`;
    scans.set(key, [...(scans.get(key) ?? []), run]);
  }
  return (
    <Box p={3} maxWidth={1000}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">LLM work</Typography>
        <Button disabled={loading} onClick={() => setRefresh((r) => r + 1)}>
          {loading && data ? "Refreshing…" : "Refresh"}
        </Button>
      </Box>
      <Typography color="text.secondary" mb={2}>
        Updates every 5 seconds while this tab is visible.
      </Typography>
      {error && (
        <Alert severity="error">
          Queue data is unavailable.
          {data
            ? " The figures below are from the last successful refresh."
            : ""}
        </Alert>
      )}
      {!data && !error && <CircularProgress aria-label="Loading LLM work" />}
      {data && s && (
        <>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
            gap={2}
          >
            {(
              [
                [
                  "GPU rental / hr (USD)",
                  data.gpu_rental
                    ? `${
                        data.gpu_rental.estimated ? "~" : ""
                      }$${data.gpu_rental.hourly_usd.toFixed(2)}`
                    : "Unavailable",
                ],
                [
                  "Backlog hours",
                  s.backlog_hours == null
                    ? s.capacity_status === "offline"
                      ? "No capacity"
                      : "Measuring…"
                    : `${s.backlog_hours.toFixed(2)} h`,
                ],
                ["Queued", s.queued],
                ["Running", s.running],
                ["Completed", s.succeeded],
                ["Failed", s.failed],
              ] as const
            ).map(([label, value]) => (
              <Paper
                key={label}
                sx={{ p: 2 }}
                title={
                  label === "GPU rental / hr (USD)"
                    ? "Supervisor-tracked automatic and manual pods, including startup and idle time, until deletion is confirmed. ~ uses the pool rate ceiling where the actual rate is unknown. Storage is separate."
                    : undefined
                }
              >
                <Typography color="text.secondary">{label}</Typography>
                <Typography variant="h5">{value.toLocaleString()}</Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption">
            Updated {stamp(s.captured_at)}
            {s.assessments_per_hour != null &&
              ` · ~${Math.round(
                s.assessments_per_hour
              ).toLocaleString()} assessments/hour. Backlog includes queued and running work at recent throughput (up to 5 minutes), assuming no new arrivals.`}
          </Typography>
          {s.queued > 0 &&
            !data.allocations.some((a) => a.active_leases > 0) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Work is queued, but no allocation has a current work lease.
                Check worker availability.
              </Alert>
            )}
          {data.shadow_admission.bridge_errors > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {data.shadow_admission.bridge_errors} shadow packet(s) have
              admission/publication errors. The bridge will retry them.
            </Alert>
          )}
          <Box mt={2}>
            <PoolControls legacyWorkers={data.allocations.filter(a => !a.id.startsWith("managed-") && !a.id.startsWith("pool:") && (a.active_leases > 0 || a.queued_packets > 0 || a.expired_leases > 0))} />
          </Box>
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Queue trend — last 24 hours</Typography>
            <Box height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="bucket"
                    tickFormatter={(v) =>
                      new Date(v).toLocaleTimeString([], { hour: "2-digit" })
                    }
                  />
                  <YAxis allowDecimals={false} />
                  <YAxis
                    yAxisId="backlog"
                    orientation="right"
                    allowDecimals={false}
                  />
                  <Tooltip labelFormatter={(v) => stamp(String(v))} />
                  <Legend />
                  <Bar
                    isAnimationActive={false}
                    dataKey="arrived"
                    name="Arrived"
                    fill="#1976d2"
                  />
                  <Bar
                    isAnimationActive={false}
                    dataKey="finished"
                    name="Finished (all outcomes)"
                    fill="#2e7d32"
                  />
                  <Bar
                    isAnimationActive={false}
                    dataKey="failed"
                    name="Failed (subset)"
                    fill="#d32f2f"
                  />
                  <Line
                    isAnimationActive={false}
                    yAxisId="backlog"
                    dataKey="pending"
                    name="Pending jobs (right axis)"
                    stroke="#ed6c02"
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            {s.historical_unknown_completion > 0 && (
              <Typography variant="caption">
                {s.historical_unknown_completion} historical terminal jobs have
                no completion timestamp and are excluded from rates.
              </Typography>
            )}
          </Paper>
          <Typography variant="h6" mt={3} mb={1}>
            Shadow scans
          </Typography>
          {scans.size === 0 && (
            <Typography color="text.secondary">
              No shadow scans awaiting results.
            </Typography>
          )}
          {[...scans.entries()].map(([key, repeats]) => {
            const first = repeats[0];
            const failed = repeats.reduce((sum, r) => sum + r.items_failed, 0);
            return (
              <Paper key={key} sx={{ p: 2, mb: 1 }}>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography
                    component="a"
                    href={`/scans/compare?need_id=${first.need_id}&batch_number=${first.baseline_batch_number}`}
                  >
                    {first.org_name || `Scan ${first.need_id}`} · batch{" "}
                    {first.baseline_batch_number}
                  </Typography>
                  <Typography variant="body2">
                    {repeats.some((r) => r.processing)
                      ? "Processing"
                      : "Waiting for GPU"}
                  </Typography>
                </Box>
                {first.need_title && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={first.need_title}
                  >
                    {first.need_title}
                  </Typography>
                )}
                {repeats.map((r) => (
                  <Box
                    key={r.match_run_id}
                    display="flex"
                    gap={2}
                    alignItems="center"
                    mt={1}
                  >
                    <Typography variant="caption" sx={{ minWidth: 60 }}>
                      Run {r.match_run_id}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        r.items_total
                          ? ((r.items_completed + r.items_failed) /
                              r.items_total) *
                            100
                          : 0
                      }
                      sx={{
                        flex: 1,
                        "& .MuiLinearProgress-bar": { transition: "none" },
                      }}
                      aria-label={`Run ${r.match_run_id} progress`}
                    />
                    <Typography
                      variant="caption"
                      sx={{ minWidth: 75, textAlign: "right" }}
                    >
                      {r.items_completed + r.items_failed} / {r.items_total}
                    </Typography>
                  </Box>
                ))}
                {failed > 0 && (
                  <Typography variant="caption" color="error">
                    {failed} failed assessments across these repeats
                  </Typography>
                )}
                {repeats
                  .filter((r) => r.error)
                  .map((r) => (
                    <Typography
                      key={r.match_run_id}
                      color="error"
                      variant="caption"
                    >
                      {r.error}
                    </Typography>
                  ))}
              </Paper>
            );
          })}
          <Typography variant="body2" color="text.secondary" mt={1}>
            {data.shadow_admission.pending_packets} packets awaiting admission;{" "}
            {data.shadow_admission.submitted_packets} awaiting results.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={3}>
            Last 15 minutes: {data.usage_15m.attempts.toLocaleString()} attempts
            · {data.usage_15m.failed_attempts} failed ·{" "}
            {data.usage_15m.prompt_tokens.toLocaleString()} input tokens ·{" "}
            {data.usage_15m.completion_tokens.toLocaleString()} output tokens.
            GPU rental is billed separately.
          </Typography>
        </>
      )}
    </Box>
  );
}
